import { Button, Card, Col, Link, Notification, PageHeader, Row, Spacer, Table, Text, Title } from '@app/components'
import { IStore } from '@app/redux'
import { metaMask, metaMaskHooks, walletConnect, walletConnectHooks } from '@app/services'
import { ETheme } from '@app/theme'
import { toCurrency } from '@app/utils'
import { Web3ReactHooks, Web3ReactProvider } from '@web3-react/core'
import { MetaMask } from '@web3-react/metamask'
import { WalletConnect } from '@web3-react/walletconnect-v2'
import axios from 'axios'
import { round } from 'lodash'
import { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { useChainId } from 'wagmi'

import { PhuxIcon, PulseXIcon } from '~public'

import { BalanceContext } from '../fortress/BalanceContext'
import { COW_SWAP_CHAIN_IDS,CowSwapEmbed } from './CowSwapEmbed'

const StyledTable = styled(Table)`
  tr.leaderboard-row-self {
    background-color: #abf3ff;
    font-weight: bold;
    td {
      &.ant-table-cell.ant-table-cell-row-hover {
        background-color: #abf3ff;
      }
    }
  }
`
//Comment for push

export const Swap = () => {
  const { fetchAccounts } = useContext(BalanceContext)
  const { theme } = useSelector((state: IStore) => state)
  const [pairsLoading, setPairsLoading] = useState(true)
  const [pairs, setPairs] = useState([])

  const connectors: [MetaMask | WalletConnect, Web3ReactHooks][] = [
    [metaMask, metaMaskHooks],
    [walletConnect, walletConnectHooks],
  ]
  const chainId = useChainId()
  const pulseChainId = parseInt(process.env.NEXT_PUBLIC_PULSECHAIN || '369', 10)
  const onPulse = chainId === pulseChainId
  const cowTheme = theme.value === ETheme.DARK ? 'dark' : 'light'

  useEffect(() => {
    fetchAccounts()
    if (onPulse) getPairs()
  }, [onPulse])

  const switchNetwork = async (targetChainId: number) => {
    try {
      await metaMask.activate(targetChainId)
      Notification({ message: 'Network switched', type: 'success' })
    } catch {
      // user rejected or wallet unavailable
    }
  }

  const getPairs = async () => {
    try {
      setPairsLoading(true)
      const response = await axios.get(
        'https://api.geckoterminal.com/api/v2/networks/pulsechain/tokens/0x600136dA8cc6D1Ea07449514604dc4ab7098dB82/pools',
      )
      const pairData = response.data.data
      pairData.sort((a, b) => {
        return compare(parseFloat(a.attributes.reserve_in_usd), parseFloat(b.attributes.reserve_in_usd))
      })
      //console.log("hitting: ", pairData)
      const pairTableData = []
      await pairData.forEach((element) => {
        if (parseFloat(element.attributes.reserve_in_usd) > 1000) {
          pairTableData.push({
            dex:
              element.relationships.dex.data.id != 'phux' ? (
                <PulseXIcon style={{ height: 20 }} />
              ) : (
                <PhuxIcon style={{ height: 20 }} />
              ),
            fdv: element.attributes.fdv_usd,
            liquidity: toCurrency(parseFloat(element.attributes.reserve_in_usd), true),
            priceUsd: round(element.attributes.token_price_usd, 4),
            quoteToken: element.attributes.name,
            //url: element.url
          })
        }
      })

      setPairs(pairTableData)
      //console.log("table data: ", pairTableData)
    } catch (error) {
      //console.log("getPairs error: ", error)
    } finally {
      setPairsLoading(false)
    }
  }

  const compare = (a: number, b: number) => {
    return a < b ? 1 : -1
  }

  const [isDesktop, setDesktop] = useState(false)

  useEffect(() => {
    if (window.innerWidth >= 1024) {
      setDesktop(true)
    } else {
      setDesktop(false)
    }

    const updateMedia = () => {
      if (window.innerWidth >= 1024) {
        setDesktop(true)
      } else {
        setDesktop(false)
      }
    }
    window.addEventListener('resize', updateMedia)
    return () => window.removeEventListener('resize', updateMedia)
  }, [])

  return (
    <Web3ReactProvider connectors={connectors}>
      {onPulse ? (
        <Row gutter={[16, 24]}>
          <Col span={24} lg={4}>
            <PageHeader disableBreadcrumb />
          </Col>

          <Col span={24} lg={16} style={{ display: 'flex', justifyContent: 'center' }}>
            <iframe
              src="https://widget.piteas.io/#/swap?theme=light&exactField=input&exactAmount=100&inputCurrency=0x600136dA8cc6D1Ea07449514604dc4ab7098dB82&outputCurrency=ETH"
              height="650px"
              width="100%"
              style={{
                border: 0,
                margin: '0',
                marginBottom: '0.5rem',
                display: 'block',
                maxWidth: '960px',
                overflow: 'hidden',
              }}
              scrolling="no"
            />
          </Col>
          <Col span={24} lg={4} />

          <Col span={24}>
            <Card title="$CST Liquidity Pairs" bodyStyle={{ padding: 0 }}>
              <Row gutter={[24, 24]} justify={'center'}>
                <Spacer value={5} />
                <Col span={23}>
                  <Text style={{ fontSize: '18px', marginTop: '5px' }}>
                    This swap module is powered by the team at <Link href={'https://piteas.io/'}>Piteas</Link>. Piteas'
                    order routing spreads your buy or sell across multiple different liquidity sources shown below. This
                    ensures that when you swap $CST for other PulseChain assets, you experience the least slippage and
                    receive the MOST coins in return.{' '}
                  </Text>
                </Col>
                <Spacer value={5} />
              </Row>

              <StyledTable
                loading={pairsLoading}
                columns={[
                  { dataIndex: 'priceUsd', title: 'Price' },
                  {
                    dataIndex: 'liquidity',
                    title: 'Liquidity',
                  },
                  isDesktop ? { dataIndex: 'quoteToken', title: 'Paired Token' } : {},
                  { dataIndex: 'dex', title: 'DEX' },
                ]}
                dataSource={pairs}
                pagination={false}
              />
            </Card>
          </Col>
        </Row>
      ) : (
        <Row gutter={[16, 24]}>
          <Col span={24} lg={4}>
            <PageHeader disableBreadcrumb />
          </Col>
          <Col span={24} lg={16} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {COW_SWAP_CHAIN_IDS.has(chainId) ? (
              <>
                <CowSwapEmbed chainId={chainId} theme={cowTheme} />
                <Text type="secondary" style={{ textAlign: 'center', marginTop: 8 }}>
                  Swap on this network via{' '}
                  <Link href="https://swap.cow.fi" target="_blank">
                    CoW Protocol
                  </Link>
                  . For PulseChain $CST,{' '}
                  <Button
                    type="link"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => switchNetwork(pulseChainId)}
                  >
                    switch to PulseChain
                  </Button>
                  .
                </Text>
              </>
            ) : (
              <Title level={3} style={{ textAlign: 'center' }}>
                This network is not supported for swap.{' '}
                <Button type="link" onClick={() => switchNetwork(pulseChainId)}>
                  Switch to PulseChain
                </Button>{' '}
                or Ethereum.
              </Title>
            )}
          </Col>
          <Col span={24} lg={4} />
        </Row>
      )}
    </Web3ReactProvider>
  )
}
